import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconDownloadOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDownloadOutline';
import { useHasPermission } from '@contexts/permissions-provider/hooks/use-has-permission/useHasPermission';
import { useDownloadInvoice } from '@features/invoices/hooks/use-download-invoice/useDownloadInvoice';
import { useInvoiceDetails } from '@features/invoices/hooks/use-invoice-details/useInvoiceDetails';
import { formatAmount } from '@features/invoices/utils/invoices.utils';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { DataValue } from '@shared/components/data-value/DataValue';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { FEATURE_FLAGS } from '@shared/constants/featureFlags.constants';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import type { InvoiceDetailPanelProps } from './InvoiceDetailPanel.types';

import { CreditMemosTableMobile } from './credit-memos-table-mobile/CreditMemosTableMobile';
import { CreditMemosTable } from './credit-memos-table/CreditMemosTable';
import styles from './InvoiceDetailPanel.module.css';
import { OutstandingAmountTile } from './outstanding-amount-tile/OutstandingAmountTile';
import { ReservationDetailsSection } from './reservation-details-section/ReservationDetailsSection';

const InvoiceDetailPanel = React.memo(
  ({
    onClose,
    accountReceivableId,
  }: Readonly<InvoiceDetailPanelProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const canDownload = useHasPermission('Invoices_Open_Org');
    const {
      invoiceDetailOriginalAmount,
      [FEATURE_FLAGS.manageInvoicesCreditMemosEnabled]: manageInvoicesCreditMemosEnabled,
    } = useFlags();
    const { invoiceDetails } = useInvoiceDetails({ accountReceivableId });
    const { data, isLoading, isError } = invoiceDetails;
    const { handleDownload, isDownloading } = useDownloadInvoice({
      accountReceivableId,
      transactionNumber: data?.transactionNumber ?? null,
    });

    return (
      <CobaltSidePanel
        size={isMobileOrTablet ? 'large' : 'medium'}
        isOpen
        showCloseButton
        onClose={onClose}
        title={
          data ? (
            data.transactionNumber ? (
              `Invoice number: ${data.transactionNumber}`
            ) : (
              <span className={styles.panelTitleEmpty}>
                Invoice number: <DataValue value={null} />
              </span>
            )
          ) : (
            'Invoice details'
          )
        }
        description={
          data && canDownload && data.isDownloadAvailable ? (
            <CobaltButton
              variant="ghost"
              className={styles.downloadButton}
              pending={isDownloading}
              data-testid="invoice-download-button"
              aria-label={`Download invoice ${data.transactionNumber || data.accountReceivableId}`}
              onClick={handleDownload}
            >
              Download
              <CobaltIconDownloadOutline size="small" aria-hidden="true" />
            </CobaltButton>
          ) : undefined
        }
        data-testid="invoice-detail-panel"
      >
        {isLoading ? (
          <ContentLoader />
        ) : isError || !data ? (
          <InlineFeedback type="data-error" />
        ) : (
          <div
            className={styles.detailPanelContent}
            data-testid="invoice-detail-panel-content"
          >
            <OutstandingAmountTile
              outstandingAmount={data.outstandingAmount}
              currency={data.currency}
              dueDate={data.dueDate}
              balanceStatus={data.balanceStatus}
              hasCreditMemos={
                manageInvoicesCreditMemosEnabled && !!data.creditMemos?.length
              }
            />
            {manageInvoicesCreditMemosEnabled &&
              data.creditMemos &&
              data.creditMemos.length > 0 && (
              <section className={styles.section}>
                <h3 className={`heading-01 ${styles.sectionTitle}`}>Credits</h3>
                {isMobileOrTablet ? (
                  <CreditMemosTableMobile creditMemos={data.creditMemos} />
                ) : (
                  <CreditMemosTable creditMemos={data.creditMemos} />
                )}
              </section>
            )}

            <section className={styles.section}>
              <h3 className={`heading-01 ${styles.sectionTitle}`}>
                Invoice details
              </h3>
              <ul className={styles.fieldList}>
                {invoiceDetailOriginalAmount && (
                  <li className={styles.field}>
                    <span className={`body-01 ${styles.fieldLabel}`}>
                      Original amount
                    </span>
                    <span className={`body-02 ${styles.fieldValue}`}>
                      <DataValue
                        value={
                          data.originalAmount != null
                            ? formatAmount(data.originalAmount, data.currency)
                            : undefined
                        }
                      />
                    </span>
                  </li>
                )}
                <li className={styles.field}>
                  <span className={`body-01 ${styles.fieldLabel}`}>
                    Issued on
                  </span>
                  <span className={`body-02 ${styles.fieldValue}`}>
                    <DataValue value={formatDateShort(data.issuedOn)} />
                  </span>
                </li>
                <li className={styles.field}>
                  <span className={`body-01 ${styles.fieldLabel}`}>
                    Legal entity
                  </span>
                  <span className={`body-02 ${styles.fieldValue}`}>
                    <DataValue value={data.legalEntityName} />
                  </span>
                </li>
                {data.poNumbers != null && data.poNumbers.length > 0 && (
                  <li className={styles.field}>
                    <span className={`body-01 ${styles.fieldLabel}`}>
                      PO number
                    </span>
                    <span className={`body-02 ${styles.fieldValue}`}>
                      <DataValue value={data.poNumbers.join(', ')} />
                    </span>
                  </li>
                )}
                {data.contractAgreement && (
                  <li className={styles.field}>
                    <span className={`body-01 ${styles.fieldLabel}`}>
                      Contract agreement
                    </span>
                    <span className={`body-02 ${styles.fieldValue}`}>
                      <DataValue value={data.contractAgreement} />
                    </span>
                  </li>
                )}
              </ul>
            </section>

            {data.reservations != null && data.reservations.some(r => r.reservationNumber != null) && (
              <ReservationDetailsSection reservations={data.reservations} />
            )}
          </div>
        )}
      </CobaltSidePanel>
    );
  }
);

InvoiceDetailPanel.displayName = 'InvoiceDetailPanel';
export { InvoiceDetailPanel };
